import { KendraConst, SearchInterface } from "@amzn/ask-legal-domain";
import { SpaceBetween, Box, Badge, StatusIndicator, TextContent, Flashbar } from "@amzn/awsui-components-react";
import { QueryResultItem }  from "@aws-sdk/client-kendra";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsUp, faThumbsDown } from "@fortawesome/free-solid-svg-icons";
import * as React from "react";
import { useAPI } from "../../../hooks/api-hook";
import { AppContext } from "../../../setup/context";
import { DateTimeFormatter } from "../../../utils/date-time-utils";
import { getInstanceIdFromResult } from "../../../utils/search-utils";
import { SearchInstanceBadge } from "./SearchInstanceBadge";
import { PageSourceLink } from "./PageSourceLink";

interface SuggestedAnswersProps {
    instanceId: string;
    result: QueryResultItem[];
    queryId: string;
}

export const SuggestedAnswers: React.FC<SuggestedAnswersProps> = ({ instanceId, result, queryId }) => {

    const context = React.useContext(AppContext);
    const [feedbackSubmitted, setFeedbackSubmitted] = React.useState<string[]>([]);
    const [feedbackStatusUpdates, setFeedbackStatusUpdates] = React.useState<Array<any>>([]);

    const submitFeedbackRunner = useAPI(
        context.getSearchAPI().feedback
    );

    const submitFeedback = React.useCallback((queryId: string, resultId: string, relevance: KendraConst.FeedbackRelevanceValue) => {
        submitFeedbackRunner.submitRun(
            SearchInterface.SubmitFeedbackInput.create({
                instanceId,
                queryId,
                relevanceFeedbackItems: [{
                    RelevanceValue: relevance,
                    ResultId: resultId
                }]
            })
        );
    }, [instanceId, submitFeedbackRunner]);

    React.useEffect(() => {
        if (submitFeedbackRunner.status === "Succeeded") {
            const tempSet = new Set(feedbackSubmitted);
            submitFeedbackRunner.data.input.relevanceFeedbackItems.forEach(i => tempSet.add(i.ResultId));
            setFeedbackSubmitted(Array.from(tempSet));

            setFeedbackStatusUpdates([{
                type: "info",
                content: "Thank you for your feedback.",
                dismissible: true,
                onDismiss: () => setFeedbackStatusUpdates([])
            }]);
        }
    }, [submitFeedbackRunner.status, feedbackSubmitted]);

    return (
        <div>
            <SpaceBetween direction="vertical" size="xs">
                <Flashbar items={feedbackStatusUpdates}/>
                {result.length === 1 && <h5>Suggested Answer</h5>}
                {result.length > 1 && <h5>Suggested Answers</h5>}
                {result.map(r => (
                    <div key={r.Id} className="search-page-suggested search-result-item">
                        <p>
                            <SpaceBetween direction="horizontal" size="xs">
                                {r.DocumentAttributes.find(a => a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.LegalOnly)?.Value.StringValue === "true" &&
                                    <Box variant="small" color="text-body-secondary" margin={{right: "xs"}} padding="n">
                                        <Badge color="green">Legal Only</Badge>
                                    </Box>}
                                <SearchInstanceBadge
                                    instanceId={getInstanceIdFromResult(r)}
                                />
                            </SpaceBetween>
                                <span style={{ color: "grey" }}>
                                    {DateTimeFormatter.toSimpleDate(r.DocumentAttributes.find(
                                        a => a.Key === KendraConst.ContainerIngestionKendraAttributes.Default.LastUpdatedDate
                                    )?.Value.StringValue)}
                                </span>&ensp;{r.DocumentExcerpt.Text}
                        </p>
                        <SpaceBetween direction="horizontal" size="m">
                            <PageSourceLink resultItem={r} instanceId={instanceId} />
                            { feedbackSubmitted.some(id => r.Id === id) ? (
                                <></>
                            ) :
                            submitFeedbackRunner.status === "Running" && submitFeedbackRunner.data.input.relevanceFeedbackItems.some(res => r.Id === res.ResultId) ? <StatusIndicator type="loading">Submitting response</StatusIndicator> :
                                <React.Fragment>
                                    <TextContent>
                                        <small><em>Is this answer helpful?</em></small>
                                    </TextContent>
                                    <span style={{ color: "darkorange", cursor: "pointer" }} onClick={() => submitFeedback(queryId, r.Id, "RELEVANT")}>
                                        <FontAwesomeIcon  icon={faThumbsUp} size={"lg"}/>
                                    </span>
                                    <span style={{ color: "darkorange", cursor: "pointer" }} onClick={() => submitFeedback(queryId, r.Id, "NOT_RELEVANT")}>
                                        <FontAwesomeIcon  icon={faThumbsDown} size={"lg"}/>
                                    </span>
                                </React.Fragment>
                            }
                        </SpaceBetween>
                    </div>
                ))}
            </SpaceBetween>
        </div>
    );
};
